import React, {useState, useEffect, useRef} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import ProductSelectAsync from "./ProductSelectAsync";
import ProductAvailability from "./ProductAvailability";
import {findProductsByQueryString} from "../../utils/requestUtils";
import Config from "../../core/config";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Cookies from "js-cookie";
import DatePicker from "react-datepicker";
import {formatDateToAmericanFormat, parseLocalDate, removeTimeFromDate} from "../../utils/utils";
import {useNavigate} from "react-router-dom";
import Loading from "../../core/Loading";
import {UserPermission} from "../../utils/utils";
import {usePermission} from "../../core/PermissionContext";

const apiUrl = Config.apiUrl;

const ProductList = () => {
    const { hasUserPermission, loadingPermissions } = usePermission();
    const [products, setProducts] = useState([]);
    const [localProducts, setLocalProducts] = useState([]);
    const [queryString, setQueryString] = useState('');
    const [showProductSelect, setShowProductSelect] = useState(false);
    const [productAvailability, setProductAvailability] = useState(undefined);
    const inputQueryString = useRef(null);
    const [loading, setLoading] = useRecoilState(loadingAtom);
    const [stockQuantityValue, setStockQuantityValue] = useState({quantity: 0, value: 0});
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const navigate = useNavigate();

    async function makeQuery() {
        try {
            setLoading(true);
            const products = await findProductsByQueryString(queryString);
            setProducts(products);
            setLocalProducts(products);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    async function makeLocalQuery() {
        try {
            setLocalProducts(products.filter(p => p.name.toUpperCase().includes(queryString.toUpperCase())));
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    useEffect(() => {
        if (inputQueryString.current) {
            inputQueryString.current.focus();
        }
        makeQuery();
    }, []);

    useEffect(() => {
        if (((!startDate && !endDate) || (startDate && endDate))) {
            makeLocalQuery();
        }
    }, [queryString, startDate, endDate]);

    useEffect(() => {
        setStockQuantityValue({
            quantity: products.reduce((acc, obj) => acc + obj.stock, 0),
            value: products.reduce((acc, obj) => acc + (obj.costValue * obj.stock), 0)
        });
    }, [products]);

    async function deleteProduct(id) {
        try {
            setLoading(true);
            const url = apiUrl + '/api/products/' + id;
            await makeRequest(url, 'DELETE', {'Useruuid': Cookies.get('userUUID')});
            await makeQuery();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handleInputChange = (e) => {
        const string = e.target.value;
        if (string.length >= 3 || string.length === 0) {
            makeQuery(string);
        }
        setQueryString(string);
    };

    const onAvailabilityClick = (product) => {
        setProductAvailability(product);
        setShowProductSelect(true);
    };

    const closeProductSelect = () => {
        setShowProductSelect(false);
    };

    const handleStartDateChange = (date) => {
        if (endDate && date > endDate) {
            setEndDate(date);
        }
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        if (date && date < startDate) {
            alert('Data final não pode ser anterior à data inicial.');
            return;
        }
        setEndDate(date);
    };

    if (loadingPermissions) {
        return <Loading></Loading>;
    }
    if (hasUserPermission(UserPermission.LIST_PRODUCT) === false) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                <div className="header-label">Consulta de Produtos</div>
                {hasUserPermission(UserPermission.CREATE_PRODUCT) &&
                    <button className={"new-button"} onClick={() => navigate('/app/product')}>Novo</button>
                }
            </div>

            {showProductSelect && (
                <ProductAvailability onClose={closeProductSelect} product={productAvailability}/>)}

            <div style={{
                position: 'relative',
                display: "flex",
                alignItems: "flex-start",
            }}>
                <div style={{display: "flex"}}>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        Nome:
                        <div style={{display: 'flex', alignItems: 'center', width: '100%'}}>
                            <input data-test="name-input" ref={inputQueryString} type="text" value={queryString}
                                   onChange={handleInputChange}/>
                        </div>
                    </div>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        Período de disponibilidade:
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <DatePicker
                                className="datepicker"
                                selected={startDate}
                                minDate={new Date()}
                                onChange={handleStartDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            até
                            <DatePicker
                                className="datepicker"
                                selected={endDate}
                                minDate={new Date()}
                                onChange={handleEndDateChange}
                                dateFormat="dd/MM/yyyy"
                                isClearable
                                autoComplete="off"
                            />
                            {((!startDate && endDate) || (startDate && !endDate)) &&
                                (<div style={{color: "red"}}>Preencha o período completo!</div>)
                            }
                        </div>
                    </div>
                </div>
            </div>

            <table style={{width: '700px', height: '400px'}}>
                <tr>
                    <th>
                        Código
                    </th>
                    <th>
                        Nome
                    </th>
                    <th>
                        Estoque
                    </th>
                    {startDate && endDate && (
                        <th>
                            Disponível
                        </th>
                    )}
                    <th>
                        Custo
                    </th>
                    <th/>
                    <th/>
                    <th/>
                </tr>
                <tbody style={{overflowY: 'auto'}}>

                {localProducts.map((product, index) => {
                    let availability = 0;
                    if (startDate && endDate) {
                        const newStartDate = removeTimeFromDate(startDate);
                        const newEndDate = removeTimeFromDate(endDate);
                        const productAvailability = product.availabilities.find(a =>
                            newStartDate >= (parseLocalDate(a.startDate)) &&
                            (!a.endDate || newEndDate <= parseLocalDate(a.endDate)));

                        if (productAvailability) {
                            availability = productAvailability.quantity;
                        }
                    }
                    return (
                    <tr key={index}>
                        <td>
                            <div>{product.code}</div>
                        </td>
                        <td>
                            <div>{product.name}</div>
                        </td>
                        <td>
                            <div>{product.stock}</div>
                        </td>
                        {startDate && endDate &&
                            (
                                <td>
                                    <div key={index}>{availability}</div>
                                </td>
                            )}
                        <td>
                            <div>R$ {product.costValue?.toFixed(2)}</div>
                        </td>
                        <td>
                            <button data-test={'product-availability-button-'+index} onClick={() => onAvailabilityClick(product)}>
                                Disponibilidade
                            </button>
                        </td>
                        <td>
                            {hasUserPermission(UserPermission.EDIT_PRODUCT) &&
                                <button className="edit-button" onClick={async () => {
                                    await navigate('/app/product?id=' + product.id);
                                }}>Editar
                                </button>
                            }
                        </td>
                        <td>
                            {hasUserPermission(UserPermission.REMOVE_PRODUCT) &&
                                <button className="remove-button" onClick={async () => {
                                    // eslint-disable-next-line no-restricted-globals
                                    if (window.confirm('Deseja realmente excluir?')) {
                                        await deleteProduct(product.id);
                                    }
                                }}>Excluir
                                </button>
                            }
                        </td>
                    </tr>
                )}
                )}
                </tbody>
                {localProducts.length === 0 && (<tfoot>{loading ? 'Carregando...' : `Nenhum item encontrado.`}</tfoot>)}
            </table>

            <div style={{marginTop: '10px'}}>
                <div className="total-label">Total de produtos: {stockQuantityValue.quantity}</div>
                <div className="total-label">Valor total em estoque: R$ {stockQuantityValue.value?.toFixed(2)}</div>
            </div>
        </>
    );
};

export default ProductList;
